import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import MaterialTable from "../../components/Table/MaterialTable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import OrganizationForm from "./OrganizationForm";

const HotelOrganization = () => {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const handleExportToExcel = () => {
    const formattedData = tableData.map((item) => ({
      "Organization Name": item.name,
      "Organization Type": item.org_type,
      Country: item.headquarters_country,
      "Tax ID": item.tax_id,
      Website: item.website_url,
      "Support Email": item.support_email,
      "Support Phone": item.support_phone,
      "Commission Rate": `${item.commission_rate}%`,
      Currency: item.currency,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "HotelOrganizations");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      data,
      `HotelOrganizations_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };
  const columns = [
    { title: "ID", field: "id" },
    { title: "Organization Name", field: "name" },
    { title: "Organization Type", field: "org_type" },
    { title: "Country", field: "headquarters_country" },
    { title: "Tax ID", field: "tax_id" },
    { title: "Website", field: "website_url" },
    { title: "Support Email", field: "support_email" },
    { title: "Support Phone", field: "support_phone" },
    { title: "Commission Rate", field: "commission_rate" },
    { title: "Currency", field: "currency" },
  ];

  const fetchData = async () => {
    try {
      const response = await fetch("https://api.esthell.com/HotelOrganization");
      if (!response.ok) throw new Error("Failed to fetch data");
      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        setTableData(responseData.data);
      } else {
        setTableData([]);
        setError("Invalid response format.");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editData?.id) {
        const response = await fetch(
          `https://api.esthell.com/HotelOrganization/${editData.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ data: formData }),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update organization");
        }
        setNotification({
          open: true,
          message: "Organization updated successfully!",
          type: "success",
        });
      } else {
        // Generate org_id automatically
        const numberingPayload = {
          warehouse_id: "WH001",
          numbering_type: "Org",
          gen_year: new Date().getFullYear(),
        };

        const numberingResponse = await fetch(
          "https://api.esthell.com/location_numbertype_mst",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(numberingPayload),
          },
        );

        if (!numberingResponse.ok) {
          throw new Error("Failed to generate organization ID");
        }

        const numberingData = await numberingResponse.json();
        // Increment the last_gen_number by 1 for the new org_id
        const nextOrgId = parseInt(numberingData[0].last_gen_number) + 1;

        // Create organization with incremented org_id
        const response = await fetch(
          "https://api.esthell.com/create/HotelOrganization",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              data: {
                ...formData,
                org_id: nextOrgId,
              },
            }),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create organization");
        }
        setNotification({
          open: true,
          message: "Organization created successfully!",
          type: "success",
        });
      }

      setShowForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  const handleEdit = (row) => {
    setEditData(row);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    try {
      const response = await fetch(
        `https://api.esthell.com/HotelOrganization/${row.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete organization");
      }

      const result = await response.json();
      if (result.success) {
        fetchData();
        setNotification({
          open: true,
          message: "Organization deleted successfully!",
          type: "success",
        });
      } else {
        throw new Error(result.message || "Failed to delete organization");
      }
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Hotel Organizations
        </Typography>
        <Button variant="outlined" onClick={handleExportToExcel}>
          Export to Excel
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ mt: "1cm", width: "100%" }}>
          <MaterialTable
            columns={columns}
            data={tableData}
            selection={false}
            actions={false}
            onEdit={handleEdit}
            onDelete={handleDelete}
            toolbar={{ create: true, onCreateClick: () => setShowForm(true) }}
            sx={{
              "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
              "& .MuiTableContainer-root": {
                maxHeight: "calc(100vh - 250px)",
                overflowX: "auto",
                overflowY: "auto",
              },
            }}
          />
        </Box>
      )}

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", overflowY: "auto" } }}
      >
        <Box sx={{ p: 3 }}>
          <OrganizationForm
            handleClose={() => {
              setShowForm(false);
              setEditData(null);
            }}
            onSubmit={handleFormSubmit}
            initialData={editData}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelOrganization;
